import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  Unauthenticated,
  type EngagementHeader,
  type Me,
  type Role,
} from "./api";

/**
 * Who is signed in.
 *
 * The role here is for *layout* only — which surface to render, which rail keys
 * exist. It is never an authorisation decision: the server re-derives identity
 * from the cookie on every request and the database decides what comes back. A
 * tampered role in this context would change what the interface offers and
 * nothing about what the API returns, which is the property worth having.
 */

interface SessionState {
  me: Me | null;
  status: "checking" | "signed-in" | "signed-out";
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Called by the API layer when any request comes back 401. */
  expired: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<SessionState["status"]>("checking");

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<Me>("/api/me");
      setMe(next);
      setStatus("signed-in");
    } catch (error) {
      if (error instanceof Unauthenticated) {
        setMe(null);
        setStatus("signed-out");
        return;
      }
      // A network failure is not a sign-out. Treating it as one would throw a
      // Lead back to the login screen every time the wifi blinked.
      setStatus(me ? "signed-in" : "signed-out");
    }
  }, [me]);

  const signOut = useCallback(async () => {
    try {
      await api.del("/api/session");
    } finally {
      setMe(null);
      setStatus("signed-out");
    }
  }, []);

  const expired = useCallback(() => {
    setMe(null);
    setStatus("signed-out");
  }, []);

  useEffect(() => {
    void refresh();
    // Deliberately once, on mount. `refresh` closes over `me` for the network
    // case above, and re-running on that would poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<SessionState>(
    () => ({ me, status, refresh, signOut, expired }),
    [me, status, refresh, signOut, expired],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession used outside SessionProvider");
  return ctx;
}

/** The firm's side, versus the client's. Decides which world a surface grounds in. */
export function isFirmSide(role: Role | undefined): boolean {
  return role !== undefined && role !== "client";
}

/**
 * What a role is CALLED, which is not what it is. The stored role is
 * `super_admin` everywhere -- schema, policies, `app.is_super_admin()`, the
 * audit trail -- and renaming that would be a migration across fifty files to
 * change a word. This function is the single place the firm's own vocabulary
 * is applied to it, so "Managing Partner" (asked for on 2026-10-28) is one
 * line here rather than a string scattered through the screens.
 */
export function roleLabel(role: Role): string {
  switch (role) {
    case "super_admin":
      return "Managing Partner";
    case "principal_partner":
      return "Principal Partner";
    case "project_lead":
      return "Project Lead";
    case "consultant":
      return "Consultant";
    case "client":
      return "Client";
  }
}

/**
 * The engagement in scope, for any surface that needs to say which one it is in.
 *
 * Principle two of this product: scope is always visible. The Files screen
 * shipped without it for one render and the sidebar simply had no engagement on
 * it — which is exactly the ambiguity the rule exists to prevent, so this makes
 * it one hook rather than something each screen remembers to pass.
 */
export function useEngagementHeader(engagementId: string | null) {
  const [header, setHeader] = useState<EngagementHeader | null>(null);

  useEffect(() => {
    if (!engagementId) {
      setHeader(null);
      return;
    }
    let live = true;
    api
      .get<EngagementHeader>(`/api/engagements/${engagementId}`)
      .then((h) => live && setHeader(h))
      // A missing label is not worth an error state: the screen's own resource
      // will report absence if the engagement is genuinely out of reach.
      .catch(() => live && setHeader(null));
    return () => {
      live = false;
    };
  }, [engagementId]);

  return header;
}
